<template>
  <!-- 隐藏的file input -->
  <input
    ref="fileInput"
    type="file"
    :accept="accept"
    style="display: none"
    @change="handleFileSelect"
  />

  <!-- 触发按钮 -->
  <slot :openFilePicker="triggerFileInput" :uploading="uploading">
    <div class="small-icon-button" @click="triggerFileInput">
      <AttachIcon />
    </div>
  </slot>
</template>

<!-- eslint-disable @typescript-eslint/no-explicit-any -->
<script setup lang="ts">
import { ref } from "vue";
import {
  DEFAULT_ACCEPT_STRING,
  DEFAULT_ALLOWED_TYPES,
  DEFAULT_ALLOWED_EXTENSIONS,
} from "../../utils/constants";
import AttachIcon from "../icon/AttachIcon.vue";

// 定义props
interface Props {
  uploadUrl: string; // 上传API地址
  maxSize?: number; // 最大文件大小，单位MB
  additionalData?: Record<string, any>; // 额外参数
  headers?: Record<string, string>; // 请求头
  accept?: string; // 接受的文件类型
  allowedTypes?: string[]; // 允许的文件类型数组（MIME types）
  allowedExtensions?: string[]; // 允许的文件扩展名
}

const props = withDefaults(defineProps<Props>(), {
  maxSize: 10, // 默认增加到10MB
  accept: DEFAULT_ACCEPT_STRING, // 使用函数生成默认值
  allowedTypes: () => DEFAULT_ALLOWED_TYPES,
  allowedExtensions: () => DEFAULT_ALLOWED_EXTENSIONS,
});

// 定义事件
const emit = defineEmits<{
  "upload:start": [];
  "upload:success": [response: any];
  "upload:error": [error: Error];
  "upload:progress": [progress: number];
}>();

// 响应式数据
const fileInput = ref<HTMLInputElement>();
const uploading = ref(false);
const uploadProgress = ref(0);

// 触发文件选择
const triggerFileInput = () => {
  fileInput.value?.click();
};

// 处理文件选择
const handleFileSelect = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];

  if (file) {
    // 验证文件
    if (!validateFile(file)) {
      target.value = ""; // 重置选择
      return;
    }

    // 执行上传
    try {
      const result = await uploadFile(file);
      console.log(result);
      emit("upload:success", result);
    } catch (error) {
      emit("upload:error", error as Error);
    }
  }

  // 重置input
  target.value = "";
};

// 验证文件
const validateFile = (file: File): boolean => {
  // 验证文件大小
  const maxSizeInBytes = props.maxSize * 1024 * 1024;
  if (file.size > maxSizeInBytes) {
    alert(`文件大小不能超过 ${props.maxSize}MB`);
    return false;
  }

  // 验证文件类型（如果有指定allowedTypes）
  if (props.allowedTypes && props.allowedTypes.length > 0) {
    if (
      !props.allowedTypes.includes(file.type.toLowerCase()) &&
      file.type !== ""
    ) {
      // 对于没有MIME type的文件（如某些特殊文件），检查扩展名
      const extension = "." + file.name.split(".").pop()?.toLowerCase();
      if (!extension || !props.allowedExtensions?.includes(extension)) {
        alert(
          `不支持的文件类型: ${file.type || "未知类型"}。支持的类型: ${props.allowedTypes.join(", ")}`,
        );
        return false;
      }
    }
  }

  // 验证文件扩展名（如果有指定allowedExtensions）
  if (props.allowedExtensions && props.allowedExtensions.length > 0) {
    const extension = "." + file.name.split(".").pop()?.toLowerCase();
    if (!extension || !props.allowedExtensions.includes(extension)) {
      alert(
        `不支持的文件扩展名: ${extension}。支持的扩展名: ${props.allowedExtensions.join(", ")}`,
      );
      return false;
    }
  }

  return true;
};

// 获取文件图标类型的函数（如果需要显示文件类型图标）
const getFileTypeIcon = (file: File): string => {
  const extension = "." + file.name.split(".").pop()?.toLowerCase();

  if (!extension) return "unknown";

  if (
    [".jpg", ".jpeg", ".png", ".gif", ".webp", ".bmp", ".svg"].includes(
      extension,
    )
  ) {
    return "image";
  } else if ([".pdf"].includes(extension)) {
    return "pdf";
  } else if ([".doc", ".docx"].includes(extension)) {
    return "word";
  } else if ([".xls", ".xlsx"].includes(extension)) {
    return "excel";
  } else if ([".ppt", ".pptx"].includes(extension)) {
    return "powerpoint";
  } else if ([".zip", ".rar", ".7z", ".tar", ".gz"].includes(extension)) {
    return "archive";
  } else if ([".mp3", ".wav", ".mp4", ".mov"].includes(extension)) {
    return "media";
  } else if ([".txt", ".csv", ".md", ".json", ".xml"].includes(extension)) {
    return "document";
  } else {
    return "unknown";
  }
};

// 上传文件
const uploadFile = async (file: File) => {
  uploading.value = true;
  uploadProgress.value = 0;

  try {
    emit("upload:start");

    // 获取文件元数据（如果是媒体文件）
    let mediaMetadata: MediaMetadata | null = null;
    if (isMediaFile(file)) {
      try {
        mediaMetadata = await extractMediaMetadata(file);
      } catch (error) {
        console.warn("无法获取媒体文件元数据:", error);
      }
    }

    const formData = new FormData();
    formData.append("file", file);

    // 添加文件信息到表单数据
    formData.append("filename", file.name);
    formData.append("filetype", file.type);
    formData.append("filesize", file.size.toString());

    // 添加媒体元数据到表单（如果有）
    if (mediaMetadata) {
      formData.append("duration", mediaMetadata.duration.toString());
      if (mediaMetadata.width > 0) {
        formData.append("width", mediaMetadata.width.toString());
        formData.append("height", mediaMetadata.height.toString());
      }
    }

    // 添加额外的表单数据
    if (props.additionalData) {
      Object.keys(props.additionalData).forEach((key) => {
        formData.append(key, props.additionalData![key]);
      });
    }

    return await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // 监听上传进度
      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          uploadProgress.value = progress;
          emit("upload:progress", progress);
        }
      });

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const responseData = JSON.parse(xhr.responseText);

            // 增强返回数据，包含文件信息和媒体时长
            const enhancedResponse = {
              ...responseData,
              fileInfo: {
                name: file.name,
                type: file.type,
                size: file.size,
                sizeFormatted: formatFileSize(file.size),
                isMedia: isMediaFile(file),
                duration: mediaMetadata?.duration || 0,
                durationFormatted: mediaMetadata
                  ? formatDuration(mediaMetadata.duration)
                  : "",
                width: mediaMetadata?.width || 0,
                height: mediaMetadata?.height || 0,
                originalFile: file, // 注意：包含原始文件对象，但可能不适合序列化
              },
            };
            // console.log(enhancedResponse)
            resolve(enhancedResponse);
          } catch {
            // 如果响应不是JSON，返回文本和文件信息
            const enhancedResponse = {
              data: xhr.responseText,
              fileInfo: {
                name: file.name,
                type: file.type,
                size: file.size,
                sizeFormatted: formatFileSize(file.size),
                isMedia: isMediaFile(file),
                duration: mediaMetadata?.duration || 0,
                durationFormatted: mediaMetadata
                  ? formatDuration(mediaMetadata.duration)
                  : "",
                width: mediaMetadata?.width || 0,
                height: mediaMetadata?.height || 0,
              },
            };
            console.log(enhancedResponse);
            resolve(enhancedResponse);
          }
        } else {
          // 失败时也返回文件信息
          const error = new Error(`上传失败: ${xhr.statusText}`);
          (error as any).cause = {
            status: xhr.status,
            statusText: xhr.statusText,
            fileInfo: {
              name: file.name,
              size: file.size,
              type: file.type,
              isMedia: isMediaFile(file),
              duration: mediaMetadata?.duration || 0,
            },
          };
          reject(error);
        }
      });

      xhr.addEventListener("error", () => {
        const error = new Error("上传过程中发生网络错误");
        (error as any).cause = {
          fileInfo: {
            name: file.name,
            size: file.size,
            type: file.type,
            isMedia: isMediaFile(file),
            duration: mediaMetadata?.duration || 0,
          },
        };
        reject(error);
      });

      xhr.open("POST", props.uploadUrl);

      // 设置请求头
      if (props.headers) {
        Object.keys(props.headers).forEach((key) => {
          xhr.setRequestHeader(key, props.headers![key]);
        });
      }

      xhr.send(formData);
    });
  } catch (error) {
    // 包装错误信息，包含文件详情
    const enhancedError =
      error instanceof Error
        ? error
        : (() => {
            const err = new Error("上传失败");
            (err as any).cause = {
              fileInfo: { name: file.name, size: file.size },
            };
            return err;
          })();

    throw enhancedError;
  } finally {
    uploading.value = false;
    uploadProgress.value = 0;
  }
};

// 新增：格式化文件大小的工具函数
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 B";

  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// 新增：判断是否是媒体文件的函数（如果在外面没定义的话）
const isMediaFile = (file: File): boolean => {
  return file.type.startsWith("audio/") || file.type.startsWith("video/");
};

// 新增：格式化时长的函数
const formatDuration = (seconds: number): string => {
  if (!seconds || seconds <= 0) return "00:00";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }

  return `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

// 新增：提取媒体元数据的函数（如果在外面没定义的话）
const extractMediaMetadata = async (file: File): Promise<MediaMetadata> => {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);

    const media = file.type.startsWith("video/")
      ? document.createElement("video")
      : document.createElement("audio");

    const timeout = setTimeout(() => {
      URL.revokeObjectURL(url);
      reject(new Error("获取元数据超时"));
    }, 10000);

    media.addEventListener("loadedmetadata", () => {
      clearTimeout(timeout);

      const metadata: MediaMetadata = {
        duration: media.duration,
        width: (media as HTMLVideoElement).videoWidth || 0,
        height: (media as HTMLVideoElement).videoHeight || 0,
        type: file.type,
        size: file.size,
        name: file.name,
      };

      URL.revokeObjectURL(url);
      resolve(metadata);
    });

    media.addEventListener("error", (e) => {
      clearTimeout(timeout);
      URL.revokeObjectURL(url);
      reject(new Error(`媒体文件错误: ${e.message}`));
    });

    media.src = url;
    media.load();
  });
};

// MediaMetadata 接口定义
interface MediaMetadata {
  duration: number;
  width: number;
  height: number;
  type: string;
  size: number;
  name: string;
}

// 暴露方法
defineExpose({
  triggerFileInput,
  uploadFile,
  getFileTypeIcon, // 暴露获取文件图标类型的方法
});
</script>

<style lang="less" scoped>
.small-icon-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  cursor: pointer;

  &:hover {
    scale: 1.05;
  }
}
</style>
